<template>
  <div class="large-file-upload">
    <!-- 音频元素 -->
    <audio ref="audioPlayer" :src="currentSong.url" preload="auto"></audio>

    <!-- 音乐播放对话框 -->
    <el-dialog
      title="要来点音乐吗？"
      :visible="showMusicDialog"
      width="300px"
      center
      custom-class="music-dialog">
      <div class="music-dialog-content">
        <i class="el-icon-headset music-icon"></i>
      </div>
      <span style="display: flex;justify-content: center;"  class="dialog-footer">
        <el-button @click="showMusicDialog = false">不用了</el-button>
        <el-button type="primary" @click="playMusic">好呀</el-button>
      </span>
    </el-dialog>

    <!-- 可拖拽音乐播放器 -->
    <div
      v-drag
      class="music-player"
      v-show="isPlaying || isPaused"
      :class="{ 'is-playing': isPlaying, 'is-minimized': isMinimized }">
      <!-- 背景图 -->
      <div class="player-bg" :style="{ backgroundImage: `url(${currentSong.cover})` }"></div>

      <!-- 主要内容 -->
      <div class="player-content">
        <!-- 歌曲信息 -->
        <div class="song-info">
          <img :src="currentSong.cover" class="song-cover" :class="{ 'rotate': isPlaying }">
          <div class="song-details">
            <span class="song-name">{{ currentSong.name }}</span>
            <span class="song-artist">{{ currentSong.artist }}</span>
          </div>
        </div>

        <!-- 控制按钮 -->
        <div class="player-controls">
          <el-button
            type="text"
            class="control-btn"
            @click="prevSong">
            <i class="el-icon-arrow-left"></i>
          </el-button>
          <el-button
            type="text"
            class="control-btn play-btn"
            @click="toggleMusic">
            <i :class="isPlaying ? 'el-icon-video-pause' : 'el-icon-video-play'"></i>
          </el-button>
          <el-button
            type="text"
            class="control-btn"
            @click="nextSong">
            <i class="el-icon-arrow-right"></i>
          </el-button>
        </div>

        <!-- 最小化按钮 -->
        <el-button
          type="text"
          class="minimize-btn"
          @click="toggleMinimize">
          <i :class="isMinimized ? 'el-icon-full-screen' : 'el-icon-minus'"></i>
        </el-button>
      </div>
    </div>

    <!-- 拖拽上传区域 -->
    <div class="upload-container"
         :class="{ 'is-dragover': isDragover }"
         @dragover.prevent="isDragover = true"
         @dragleave.prevent="isDragover = false"
         @drop.prevent="handleDrop">
      <el-upload
        class="upload-area"
        drag
        action=""
        :auto-upload="false"
        :show-file-list="false"
        :on-change="handleFileChange"
        multiple>
        <div class="upload-content">
          <i class="el-icon-upload upload-icon"></i>
          <div class="el-upload__text">
            <span class="upload-text">将文件拖到此处，或</span>
            <em class="upload-link">点击上传</em>
          </div>
          <div class="el-upload__tip">
            支持任意格式文件，单个文件不超过5GB
          </div>
        </div>
      </el-upload>
    </div>

    <!-- 添加飞行员动画，仅在没有文件时显示 -->
    <div class="empty-state" v-if="fileList.length === 0">
      <div class="astronaut-animation">
        <div class="space-background">
          <div class="stars"></div>
          <div class="stars2"></div>
          <div class="stars3"></div>
        </div>

        <div class="astronaut">
          <!-- 宇航员头盔 -->
          <div class="helmet">
            <div class="visor">
              <div class="visor-reflection"></div>
            </div>
            <div class="helmet-lights">
              <div class="light left"></div>
              <div class="light right"></div>
            </div>
          </div>

          <!-- 宇航服 -->
          <div class="suit">
            <div class="torso">
              <div class="badge">NASA</div>
              <div class="controls">
                <div class="control"></div>
                <div class="control"></div>
                <div class="control"></div>
              </div>
            </div>
            <div class="arms">
              <div class="arm left"></div>
              <div class="arm right"></div>
            </div>
            <div class="legs">
              <div class="leg left"></div>
              <div class="leg right"></div>
            </div>
            <div class="backpack">
              <div class="thruster left"></div>
              <div class="thruster right"></div>
            </div>
          </div>

          <!-- 推进器效果 -->
          <div class="thrusters">
            <div class="thrust left">
              <div class="particle"></div>
              <div class="particle"></div>
              <div class="particle"></div>
            </div>
            <div class="thrust right">
              <div class="particle"></div>
              <div class="particle"></div>
              <div class="particle"></div>
            </div>
          </div>

          <!-- 漂浮物体 -->
          <div class="floating-items">
            <div class="satellite"></div>
            <div class="debris"></div>
            <div class="tool"></div>
          </div>
        </div>
      </div>
      <p class="empty-text">准备发射！开始你的太空上传之旅</p>
    </div>

    <!-- 文件列表 -->
    <transition-group name="file-list" tag="div" class="file-list" v-else>
      <div class="file-item"
           v-for="(file, index) in fileList"
           :key="file.name"
           :class="{ 'is-uploading': file.status === 'uploading' }">
        <!-- 文件信息 -->
        <div class="file-info">
          <div class="file-icon">
            <i :class="getFileIcon(file.raw.type)"></i>
          </div>
          <div class="file-details">
            <div class="file-name-wrapper">
              <span class="file-name" :title="file.name">{{ file.name }}</span>
              <span class="file-size">{{ formatSize(file.size) }}</span>
            </div>
            <el-progress
              :percentage="file.percentage"
              :status="getProgressStatus(file.status)"
              :stroke-width="4"
              class="progress-bar">
            </el-progress>
          </div>
        </div>

        <!-- 操作按钮 -->
        <div class="operations">
          <el-button-group>
            <el-button
              v-if="file.status === 'ready' || file.status === 'pause'"
              type="primary"
              size="mini"
              :icon="file.status === 'pause' ? 'el-icon-video-play' : 'el-icon-upload'"
              @click="handleUpload(file)">
              {{ file.status === 'pause' ? '继续' : '上传' }}
            </el-button>
            <el-button
              v-if="file.status === 'uploading'"
              type="warning"
              size="mini"
              icon="el-icon-video-pause"
              @click="handlePause(file)">
              暂停
            </el-button>
            <el-button
              v-if="file.status === 'error'"
              type="primary"
              size="mini"
              icon="el-icon-refresh"
              @click="handleRetry(file)">
              重试
            </el-button>
            <el-button
              type="danger"
              size="mini"
              icon="el-icon-delete"
              @click="handleDelete(index)">
              删除
            </el-button>
          </el-button-group>

          <el-tag
            :type="getStatusType(file.status)"
            size="mini"
            class="status-tag">
            {{ getStatusText(file.status) }}
          </el-tag>
        </div>
      </div>
    </transition-group>

    <!-- 底部统计信息 -->
    <transition name="fade">
      <div class="upload-footer" v-if="fileList.length > 0">
        <div class="statistics">
          <div class="stat-item">
            <i class="el-icon-document"></i>
            <span>总文件：{{ fileList.length }}</span>
          </div>
          <div class="stat-item">
            <i class="el-icon-download"></i>
            <span>总大小：{{ getTotalSize() }}</span>
          </div>
          <div class="stat-item">
            <i class="el-icon-warning"></i>
            <span>失败：{{ getFailedCount() }}</span>
          </div>
        </div>
        <div class="actions">
          <el-button
            type="primary"
            icon="el-icon-upload2"
            :disabled="!hasReadyFiles()"
            @click="uploadAll">
            一键上传
          </el-button>
          <el-button
            icon="el-icon-delete"
            @click="clearAll">
            清空列表
          </el-button>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import OSS from 'ali-oss'
import musicUrl from '../assets/music/moonlight.mp3'

// 优化拖拽指令
const drag = {
  bind(el) {
    el.style.position = 'fixed'
    el.style.cursor = 'move'
    el.style.transition = 'none' // 拖动时移除过渡效果
    el.style.userSelect = 'none' // 防止拖动时选中文本

    let isDragging = false
    let startX = 0
    let startY = 0
    let initialX = 0
    let initialY = 0

    const handleMouseDown = (e) => {
      // 只允许从播放器头部拖动
      const rect = el.getBoundingClientRect()
      const clickY = e.clientY - rect.top
      if (clickY > 40) return // 只允许从顶部40px区域拖动

      isDragging = true
      startX = e.clientX - el.offsetLeft
      startY = e.clientY - el.offsetTop
      initialX = el.offsetLeft
      initialY = el.offsetTop

      // 添加拖动时的样式
      el.style.opacity = '0.95'
      el.style.transform = 'scale(1.02)'
    }

    const handleMouseMove = (e) => {
      if (!isDragging) return

      e.preventDefault()
      requestAnimationFrame(() => {
        let left = e.clientX - startX
        let top = e.clientY - startY

        // 防止拖出屏幕
        const maxLeft = window.innerWidth - el.offsetWidth
        const maxTop = window.innerHeight - el.offsetHeight

        left = Math.min(maxLeft, Math.max(0, left))
        top = Math.min(maxTop, Math.max(0, top))

        // 使用 transform 代替 left/top 获得更好的性能
        const deltaX = left - initialX
        const deltaY = top - initialY
        el.style.transform = `translate3d(${deltaX}px, ${deltaY}px, 0) scale(1.02)`
      })
    }

    const handleMouseUp = () => {
      if (!isDragging) return
      isDragging = false

      // 恢复正常样式
      el.style.opacity = '1'
      el.style.transform = 'scale(1)'

      // 更新最终位置
      const transform = window.getComputedStyle(el).transform
      const matrix = new DOMMatrix(transform)
      el.style.left = (initialX + matrix.m41) + 'px'
      el.style.top = (initialY + matrix.m42) + 'px'
      el.style.transform = 'none'

      // 添加结束动画
      el.style.transition = 'transform 0.2s ease, opacity 0.2s ease'
    }

    // 使用 passive 选项提高性能
    el.addEventListener('mousedown', handleMouseDown, { passive: true })
    document.addEventListener('mousemove', handleMouseMove, { passive: false })
    document.addEventListener('mouseup', handleMouseUp, { passive: true })

    // 保存事件处理函数以便解绑
    el._dragHandlers = {
      handleMouseDown,
      handleMouseMove,
      handleMouseUp
    }
  },

  unbind(el) {
    // 清理事件监听
    const { handleMouseDown, handleMouseMove, handleMouseUp } = el._dragHandlers
    el.removeEventListener('mousedown', handleMouseDown)
    document.removeEventListener('mousemove', handleMouseMove)
    document.removeEventListener('mouseup', handleMouseUp)
    delete el._dragHandlers
  }
}

export default {
  name: 'LargeFileUpload',
  directives: {
    drag
  },
  data() {
    return {
      fileList: [], // 文件列表
      ossClient: null, // OSS客户端实例
      chunkSize: 1024 * 1024, // 分片大小：1MB
      isDragover: false,
      showMusicDialog: false,
      isPlaying: false,
      isPaused: false,
      isMinimized: false,
      currentTime: 0,
      duration: 0,
      musicUrl: musicUrl,
      playlist: [

        {
          name: "不知道是什么歌",
          artist: "随机播放啊",
          cover: "http://mz-app-develop.oss-cn-beijing.aliyuncs.com/mz_pc/guci.jpg",
          url: "http://music.163.com/song/media/outer/url?id=2665637411",
        },
        {
          name: "不知道是什么歌",
          artist: "随机播放啊",
          cover: "http://mz-app-develop.oss-cn-beijing.aliyuncs.com/mz_pc/guci.jpg",
          url: "http://music.163.com/song/media/outer/url?id=2665637412",
        },

      ],
      currentSongIndex: 0
    }
  },
  computed: {
    currentSong() {
      return this.playlist[this.currentSongIndex]
    }
  },
  created() {
    this.initOssClient()
  },
  mounted() {
    const audio = this.$refs.audioPlayer
    audio.addEventListener('timeupdate', this.updateTime)
    audio.addEventListener('loadedmetadata', this.setDuration)
    audio.addEventListener('ended', this.handleEnded)
  },
  beforeDestroy() {
    const audio = this.$refs.audioPlayer
    audio.removeEventListener('timeupdate', this.updateTime)
    audio.removeEventListener('loadedmetadata', this.setDuration)
    audio.removeEventListener('ended', this.handleEnded)
  },
  methods: {
    // 修改 OSS 客户端初始化方法
    initOssClient() {
      try {
        this.ossClient = new OSS({
          region: 'oss-cn-beijing',
          accessKeyId: 'LTAI5tSgyjpiqFK4aGi2geXi',
          accessKeySecret: 'IQd3MjCjqmxnZLaDjmH9bH1pMS6vJh',
          bucket: 'mz-app-develop',
          timeout: 120000,
          secure: true,
          headers: {
            // 添加 CORS 相关配置
            'Access-Control-Allow-Origin': '*',
            'Access-Control-Allow-Methods': 'PUT,POST,GET,DELETE,OPTIONS',
            'Access-Control-Allow-Headers': 'x-oss-meta-*,x-oss-*,ETag,Content-Type,Content-Length',
            'Access-Control-Expose-Headers': 'ETag',
            'Access-Control-Max-Age': '86400',
          },
          // 添加自定义配置
          customConfiguration: {
            'x-oss-meta-*': true,
            'x-oss-*': true,
            'ETag': true,
            'Content-Type': true,
            'Content-Length': true
          }
        })
      } catch (error) {
        console.error('OSS初始化失败:', error)
        this.$message.error('OSS初始化失败，请刷新页面重试')
      }
    },

    // 处理拖拽事件
    handleDrop(event) {
      event.preventDefault()
      this.isDragover = false

      const files = event.dataTransfer.files
      for (let i = 0; i < files.length; i++) {
        const file = files[i]
        this.handleFileChange({ raw: file })
      }
    },

    // 处理文件选择
    handleFileChange(file) {
      const fileObj = {
        raw: file.raw,
        name: file.name,
        size: file.size,
        type: file.raw.type,
        percentage: 0,
        status: 'ready', // ready, uploading, success, error, pause
        uploadId: '',
        chunks: [],
        currentChunk: 0
      }
      this.fileList.push(fileObj)

      // 如果是第一个文件，显示音乐对话框
      if (this.fileList.length === 1) {
        this.showMusicDialog = true
      }
    },

    // 处理单个文件上传
    async handleUpload(file) {
      if (file.status === 'uploading') return

      try {
        file.status = 'uploading'
        const objectName = `uploads/${Date.now()}-${file.name}` // 添加时间戳和目录前缀

        // 如果是暂停后继续，使用已有的uploadId
        if (!file.uploadId) {
          try {
            // 初始化分片上传
            const result = await this.ossClient.initMultipartUpload(objectName, {
              headers: {
                'Content-Type': file.raw.type,
                'x-oss-forbid-overwrite': true
              }
            })
            file.uploadId = result.uploadId
            file.objectName = objectName // 保存对象名称

            // 计算分片
            const chunks = Math.ceil(file.size / this.chunkSize)
            file.chunks = Array(chunks).fill().map((_, index) => ({
              number: index + 1,
              uploaded: false,
              retries: 0 // 添加重试计数
            }))
          } catch (error) {
            console.error('初始化分片上传失败:', error)
            file.status = 'error'
            this.$message.error(`文件 ${file.name} 初始化上传失败: ${error.message}`)
            return
          }
        }

        // 上传分片
        const maxRetries = 3 // 最大重试次数
        while (file.currentChunk < file.chunks.length) {
          if (file.status === 'pause') break

          const chunk = file.chunks[file.currentChunk]
          const start = file.currentChunk * this.chunkSize
          const end = Math.min(start + this.chunkSize, file.size)
          const blob = file.raw.slice(start, end)

          try {
            const result = await this.ossClient.uploadPart(
              file.objectName,
              file.uploadId,
              chunk.number,
              blob,
              {
                timeout: 120000,
                headers: {
                  'Content-Length': blob.size,
                  'Access-Control-Expose-Headers': 'ETag'
                },
                // 添加进度回调
                progress: (p, checkpoint) => {
                  const totalPercentage = ((file.currentChunk / file.chunks.length) + (p / file.chunks.length)) * 100
                  file.percentage = Math.floor(totalPercentage)
                }
              }
            )

            chunk.uploaded = true
            chunk.etag = result.etag
            file.currentChunk++
          } catch (error) {
            console.error('分片上传失败:', error)
            chunk.retries++

            if (chunk.retries <= maxRetries) {
              // 重试当前分片
              console.log(`重试第 ${chunk.number} 个分片，第 ${chunk.retries} 次重试`)
              continue
            }

            file.status = 'error'
            this.$message.error(`文件 ${file.name} 上传失败: ${error.message}`)
            return
          }
        }

        // 完成上传
        if (file.currentChunk === file.chunks.length) {
          try {
            const parts = file.chunks.map(chunk => ({
              number: chunk.number,
              etag: chunk.etag
            }))

            await this.ossClient.completeMultipartUpload(
              file.objectName,
              file.uploadId,
              parts
            )

            file.status = 'success'
            file.percentage = 100
            this.$message.success(`文件 ${file.name} 上传成功`)
          } catch (error) {
            console.error('完成上传失败:', error)
            file.status = 'error'
            this.$message.error(`文件 ${file.name} 完成上传失败: ${error.message}`)
          }
        }
      } catch (error) {
        console.error('上传过程发生错误:', error)
        file.status = 'error'
        this.$message.error(`文件 ${file.name} 上传失败: ${error.message}`)
      }
    },

    // 下载文件
    async handleDownload(file) {
      try {
        const result = await this.ossClient.get(file.name)
        const blob = new Blob([result.content])
        const downloadLink = document.createElement('a')
        downloadLink.href = URL.createObjectURL(blob)
        downloadLink.download = file.name
        document.body.appendChild(downloadLink)
        downloadLink.click()
        document.body.removeChild(downloadLink)
        URL.revokeObjectURL(downloadLink.href)

        this.$message.success(`文件 ${file.name} 下载成功`)
      } catch (error) {
        console.error('Download error:', error)
        this.$message.error(`文件 ${file.name} 下载失败: ${error.message}`)
      }
    },

    // 下载所有成功上传的文件
    async downloadAll() {
      const successFiles = this.fileList.filter(file => file.status === 'success')
      if (successFiles.length === 0) {
        this.$message.warning('没有可下载的文件')
        return
      }

      for (const file of successFiles) {
        await this.handleDownload(file)
      }
    },

    // 暂停上传
    handlePause(file) {
      if (file.status === 'uploading') {
        file.status = 'pause'
        this.$message.info(`文件 ${file.name} 已暂停上传`)
      }
    },

    // 删除文件
    async handleDelete(index) {
      const file = this.fileList[index]

      // 如果文件已上传成功，同时删除OSS上的文件
      if (file.status === 'success') {
        try {
          await this.ossClient.delete(file.name)
        } catch (error) {
          console.error('Delete error:', error)
        }
      }

      // 如果文件正在分片上传，取消上传
      if (file.uploadId) {
        try {
          await this.ossClient.abortMultipartUpload(file.name, file.uploadId)
        } catch (error) {
          console.error('Abort upload error:', error)
        }
      }

      this.fileList.splice(index, 1)
      this.$message.success(`文件 ${file.name} 已删除`)
    },

    // 上传所有文件
    uploadAll() {
      const readyFiles = this.fileList.filter(
        file => file.status === 'ready' || file.status === 'error'
      )
      if (readyFiles.length === 0) {
        this.$message.warning('没有待上传的文件')
        return
      }

      readyFiles.forEach(file => this.handleUpload(file))
    },

    // 清空列表
    async clearAll() {
      // 取消所有进行中的上传
      for (const file of this.fileList) {
        if (file.uploadId) {
          try {
            await this.ossClient.abortMultipartUpload(file.name, file.uploadId)
          } catch (error) {
            console.error('Abort upload error:', error)
          }
        }
      }

      this.fileList = []
      this.$message.success('文件列表已清空')
    },

    // 获取文件图标
    getFileIcon(type) {
      // 根据文件类型返回对应的图标类名
      if (type.includes('image')) return 'el-icon-picture'
      if (type.includes('video')) return 'el-icon-video-camera'
      if (type.includes('audio')) return 'el-icon-headset'
      return 'el-icon-document'
    },

    // 格式化文件大小
    formatSize(bytes) {
      if (bytes === 0) return '0 B'
      const k = 1024
      const sizes = ['B', 'KB', 'MB', 'GB', 'TB']
      const i = Math.floor(Math.log(bytes) / Math.log(k))
      return (bytes / Math.pow(k, i)).toFixed(2) + ' ' + sizes[i]
    },

    // 获取状态类型
    getStatusType(status) {
      const types = {
        ready: 'info',
        uploading: 'warning',
        success: 'success',
        error: 'danger',
        pause: 'info'
      }
      return types[status]
    },

    // 获取状态文本
    getStatusText(status) {
      const texts = {
        ready: '待上传',
        uploading: '上传中',
        success: '已完成',
        error: '上传失败',
        pause: '已暂停'
      }
      return texts[status]
    },

    // 获取总大小
    getTotalSize() {
      const totalBytes = this.fileList.reduce((sum, file) => sum + file.size, 0)
      return this.formatSize(totalBytes)
    },

    // 获取失败数量
    getFailedCount() {
      return this.fileList.filter(file => file.status === 'error').length
    },

    // 检查是否有待上传文件
    hasReadyFiles() {
      return this.fileList.some(
        file => file.status === 'ready' || file.status === 'error'
      )
    },

    // 播放音乐
    playMusic() {
      const audio = this.$refs.audioPlayer
      const playPromise = audio.play()

      if (playPromise !== undefined) {
        playPromise.then(() => {
          this.isPlaying = true
          this.isPaused = false
          this.showMusicDialog = false
        }).catch(error => {
          console.error('播放失败:', error)
          this.$message.error('音乐加载失败，请稍后重试')
          this.showMusicDialog = false
        })
      }
    },

    toggleMusic() {
      const audio = this.$refs.audioPlayer
      if (this.isPlaying) {
        audio.pause()
        this.isPlaying = false
        this.isPaused = true
      } else {
        const playPromise = audio.play()
        if (playPromise !== undefined) {
          playPromise.then(() => {
            this.isPlaying = true
            this.isPaused = false
          }).catch(error => {
            console.error('播放失败:', error)
            this.$message.error('音乐加载失败，请稍后重试')
          })
        }
      }
    },

    prevSong() {
      this.currentSongIndex = (this.currentSongIndex - 1 + this.playlist.length) % this.playlist.length
      this.$nextTick(() => {
        this.playMusic()
      })
    },

    nextSong() {
      this.currentSongIndex = (this.currentSongIndex + 1) % this.playlist.length
      this.$nextTick(() => {
        this.playMusic()
      })
    },

    updateTime() {
      this.currentTime = this.$refs.audioPlayer.currentTime
    },

    setDuration() {
      this.duration = this.$refs.audioPlayer.duration
    },

    handleTimeChange(time) {
      this.$refs.audioPlayer.currentTime = time
    },

    handleEnded() {
      this.nextSong()
    },

    toggleMinimize() {
      this.isMinimized = !this.isMinimized
    },

    // 修改 getProgressStatus 方法
    getProgressStatus(status) {
      // Progress 组件只接受这些状态值：success / exception / warning
      switch (status) {
        case 'success':
          return 'success'
        case 'error':
          return 'exception'
        case 'pause':
          return 'warning'
        default:
          return '' // 对于 uploading 和 ready 状态返回空字符串
      }
    },

    // 添加重试方法
    handleRetry(file) {
      // 重置文件状态
      file.status = 'ready'
      file.percentage = 0
      file.uploadId = ''
      file.chunks = []
      file.currentChunk = 0

      // 立即开始上传
      this.handleUpload(file)
    }
  }
}
</script>

<style scoped>
.large-file-upload {
  padding: 20px;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 12px 0 rgba(0,0,0,0.1);
}

.upload-container {
  position: relative;
  transition: all 0.3s ease-in-out;
  border: 2px dashed #dcdfe6;
  border-radius: 16px;
  overflow: hidden;
  background: linear-gradient(145deg, #f8f9fa, #ffffff);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05);
  margin-bottom: 30px;
  padding: 20px;
}

.upload-container.is-dragover {
  border-color: #409EFF;
  background: rgba(64, 158, 255, 0.05);
  transform: scale(1.02);
  box-shadow: 0 6px 24px rgba(64, 158, 255, 0.15);
}

.upload-content {
  text-align: center;
  transition: all 0.3s ease;
}

.el-upload-dragger{
  height: 230px !important;
  width: 100% !important;
}

.el-upload{
  width: 100%;
}

.upload-icon {
  font-size: 64px;
  color: #409EFF;
  margin-bottom: 24px;
  transition: all 0.4s ease;
  display: inline-block;
  opacity: 0.8;
}

.upload-container:hover .upload-icon {
  transform: translateY(-8px) scale(1.1);
  opacity: 1;
  color: #66b1ff;
}

>>> .el-upload-dragger{
  height: 240px;
  width: 100%;
}

>>> .el-upload{
  width: 100%;
}

.upload-text {
  color: #606266;
  font-size: 18px;
  font-weight: 500;
  margin-bottom: 8px;
  display: inline-block;
}

.upload-link {
  color: #409EFF;
  font-style: normal;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  border-bottom: 2px solid transparent;
  padding-bottom: 2px;
}

.upload-link:hover {
  color: #66b1ff;
  border-bottom-color: #66b1ff;
}

.el-upload__tip {
  margin-top: 16px;
  color: #909399;
  font-size: 14px;
  line-height: 1.4;
  opacity: 0.8;
  transition: opacity 0.3s ease;
}

.upload-container:hover .el-upload__tip {
  opacity: 1;
}

/* 添加动画效果 */
@keyframes float {
  0% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-10px);
  }
  100% {
    transform: translateY(0px);
  }
}

.is-dragover .upload-icon {
  animation: float 2s ease-in-out infinite;
  color: #409EFF;
}

/* 响应式调整 */
@media screen and (max-width: 768px) {
  .upload-container {
    margin: 15px;
  }

  .upload-content {
    padding: 30px 15px;
  }

  .upload-icon {
    font-size: 48px;
    margin-bottom: 16px;
  }

  .upload-text {
    font-size: 16px;
  }
}

.file-list {
  margin-top: 20px;
}

.file-item {
  display: flex;
  align-items: center;
  padding: 15px;
  margin-bottom: 10px;
  background: #f8f9fa;
  border-radius: 8px;
  transition: all 0.3s;
}

.file-item:hover {
  transform: translateX(5px);
  box-shadow: 0 2px 12px 0 rgba(0,0,0,0.1);
}

.file-item.is-uploading {
  background: #ecf5ff;
}

.file-info {
  flex: 1;
  display: flex;
  align-items: center;
  margin-right: 20px;
}

.file-icon {
  font-size: 24px;
  color: #409EFF;
  margin-right: 15px;
}

.file-details {
  flex: 1;
}

.file-name-wrapper {
  display: flex;
  align-items: center;
  margin-bottom: 8px;
}

.file-name {
  font-size: 14px;
  color: #303133;
  margin-right: 10px;
  max-width: 300px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.file-size {
  color: #909399;
  font-size: 12px;
}

.progress-bar {
  margin-top: 5px;
}

.operations {
  display: flex;
  align-items: center;
  gap: 15px;
}

.status-tag {
  min-width: 60px;
  text-align: center;
}

.upload-footer {
  margin-top: 20px;
  padding: 15px;
  background: #f8f9fa;
  border-radius: 8px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.statistics {
  display: flex;
  gap: 20px;
}

.stat-item {
  display: flex;
  align-items: center;
  gap: 5px;
  color: #606266;
}

.stat-item i {
  color: #409EFF;
}

.actions {
  display: flex;
  gap: 10px;
}

/* 动画效果 */
.file-list-enter-active,
.file-list-leave-active {
  transition: all 0.5s;
}

.file-list-enter,
.file-list-leave-to {
  opacity: 0;
  transform: translateY(30px);
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}

/* 响应式设计 */
@media screen and (max-width: 768px) {
  .file-item {
    flex-direction: column;
    align-items: stretch;
  }

  .operations {
    margin-top: 10px;
    justify-content: space-between;
  }

  .upload-footer {
    flex-direction: column;
    gap: 15px;
  }

  .statistics {
    flex-wrap: wrap;
    justify-content: center;
  }
}

/* 添加新的样式 */
.music-dialog {
  border-radius: 12px;
  overflow: hidden;
}

.music-dialog-content {
  text-align: center;
  padding: 20px 0;
}

.music-icon {
  font-size: 48px;
  color: #409EFF;
  margin-bottom: 15px;
  animation: pulse 2s infinite;
}

.music-player {
  position: fixed;
  top: 20px;
  right: 20px;
  width: 280px;
  height: 80px;
  background: rgba(255, 255, 255, 0.95);
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  z-index: 1000;
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);
  cursor: move;
  will-change: transform; /* 提示浏览器优化动画性能 */
  transform: translate3d(0, 0, 0); /* 强制启用硬件加速 */
  backface-visibility: hidden; /* 防止闪烁 */
  perspective: 1000px; /* 3D 效果 */
  touch-action: none; /* 防止触摸设备的默认行为 */
}

.music-player:hover {
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.15);
  transform: translateY(-2px);
}

.music-player.is-minimized {
  width: 60px;
  height: 60px;
  border-radius: 30px;
}

.player-bg {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-size: cover;
  background-position: center;
  filter: blur(20px);
  opacity: 0.2;
  z-index: -1;
  transform: scale(1.2);
}

.player-content {
  position: relative;
  height: 100%;
  display: flex;
  align-items: center;
  padding: 10px;
  gap: 10px;
}

.song-info {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
}

.song-cover {
  width: 50px;
  height: 50px;
  border-radius: 25px;
  object-fit: cover;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.song-cover.rotate {
  animation: rotate 20s linear infinite;
}

.song-details {
  flex: 1;
  min-width: 0;
}

.song-name {
  display: block;
  font-size: 14px;
  font-weight: 500;
  margin-bottom: 4px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  color: #303133;
}

.song-artist {
  display: block;
  font-size: 12px;
  color: #909399;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.player-controls {
  display: flex;
  align-items: center;
  gap: 8px;
}

.control-btn {
  padding: 6px;
  font-size: 16px;
  color: #409EFF;
}

.control-btn:hover {
  color: #66b1ff;
  transform: scale(1.1);
}

.play-btn {
  transform: scale(1.2);
}

.minimize-btn {
  position: absolute;
  top: 5px;
  right: 5px;
  padding: 4px;
  font-size: 14px;
  opacity: 0;
  transition: opacity 0.3s;
}

.music-player:hover .minimize-btn {
  opacity: 1;
}

@keyframes rotate {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

/* 最小化状态 */
.is-minimized .player-controls,
.is-minimized .song-details {
  display: none;
}

.is-minimized .song-cover {
  width: 40px;
  height: 40px;
  margin: 0 auto;
}

.is-minimized .song-info {
  justify-content: center;
  width: 100%;
}

.is-minimized .minimize-btn {
  opacity: 1;
}

/* 添加重试按钮样式 */
.el-button.el-button--primary[icon="el-icon-refresh"] {
  background-color: #67c23a;
  border-color: #67c23a;
}

.el-button.el-button--primary[icon="el-icon-refresh"]:hover {
  background-color: #85ce61;
  border-color: #85ce61;
}

/* 拖动时的样式 */
.music-player:active {
  cursor: move;
}

/* 修改动画容器样式 */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  margin-top: 40px;
  min-height: 400px;
}

.astronaut-animation {
  width: 100%;
  height: 300px;
  position: relative;
  background: linear-gradient(180deg, #0B0B2A 0%, #1B1B4B 100%);
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
}

.space-background {
  position: absolute;
  width: 100%;
  height: 100%;
  overflow: hidden;
}

.stars, .stars2, .stars3 {
  position: absolute;
  width: 100%;
  height: 100%;
  background: transparent;
}

.stars {
  background-image: radial-gradient(2px 2px at 20px 30px, #fff, rgba(0,0,0,0)),
                    radial-gradient(2px 2px at 40px 70px, #fff, rgba(0,0,0,0)),
                    radial-gradient(2px 2px at 50px 160px, #fff, rgba(0,0,0,0)),
                    radial-gradient(2px 2px at 90px 40px, #fff, rgba(0,0,0,0));
  background-repeat: repeat;
  background-size: 200px 200px;
  animation: twinkle 4s linear infinite;
}

.stars2 {
  background-image: radial-gradient(2px 2px at 15px 15px, #fff, rgba(0,0,0,0)),
                    radial-gradient(2px 2px at 45px 65px, #fff, rgba(0,0,0,0)),
                    radial-gradient(2px 2px at 60px 170px, #fff, rgba(0,0,0,0)),
                    radial-gradient(2px 2px at 95px 45px, #fff, rgba(0,0,0,0));
  background-repeat: repeat;
  background-size: 200px 200px;
  animation: twinkle 6s linear infinite;
}

.stars3 {
  background-image: radial-gradient(2px 2px at 10px 20px, #fff, rgba(0,0,0,0)),
                    radial-gradient(2px 2px at 35px 60px, #fff, rgba(0,0,0,0)),
                    radial-gradient(2px 2px at 55px 165px, #fff, rgba(0,0,0,0)),
                    radial-gradient(2px 2px at 85px 35px, #fff, rgba(0,0,0,0));
  background-repeat: repeat;
  background-size: 200px 200px;
  animation: twinkle 8s linear infinite;
}

.astronaut {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  animation: float 6s ease-in-out infinite;
}

.helmet {
  width: 80px;
  height: 80px;
  background: #fff;
  border-radius: 50%;
  position: relative;
  box-shadow: inset 0 0 20px rgba(0,0,0,0.2);
}

.visor {
  position: absolute;
  width: 60px;
  height: 30px;
  background: #000;
  border-radius: 10px;
  top: 25px;
  left: 10px;
  overflow: hidden;
}

.visor-reflection {
  position: absolute;
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, rgba(255,255,255,0.4) 0%, rgba(255,255,255,0) 50%);
  animation: visorReflect 4s ease-in-out infinite;
}

.suit {
  width: 120px;
  height: 160px;
  background: #fff;
  border-radius: 40px;
  margin-top: -20px;
  position: relative;
  box-shadow: inset 0 0 30px rgba(0,0,0,0.2);
}

.badge {
  position: absolute;
  top: 20px;
  left: 20px;
  padding: 4px 8px;
  background: #FF4B4B;
  color: #fff;
  font-size: 12px;
  border-radius: 4px;
}

.controls {
  position: absolute;
  top: 50px;
  right: 20px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.control {
  width: 15px;
  height: 15px;
  background: #333;
  border-radius: 50%;
  animation: blink 2s infinite;
}

.thrusters {
  position: absolute;
  bottom: -20px;
  width: 100%;
}

.thrust {
  position: absolute;
  width: 20px;
  height: 40px;
}

.thrust.left { left: 20px; }
.thrust.right { right: 20px; }

.particle {
  position: absolute;
  width: 8px;
  height: 8px;
  background: #66b1ff;
  border-radius: 50%;
  animation: thrust 1s ease-in infinite;
}

@keyframes float {
  0%, 100% { transform: translate(-50%, -50%) rotate(2deg); }
  50% { transform: translate(-50%, -60%) rotate(-2deg); }
}

@keyframes twinkle {
  from { transform: translateY(0); }
  to { transform: translateY(-200px); }
}

@keyframes visorReflect {
  0%, 100% { transform: translateX(-100%); }
  50% { transform: translateX(100%); }
}

@keyframes thrust {
  0% { transform: translateY(0) scale(1); opacity: 1; }
  100% { transform: translateY(40px) scale(0.2); opacity: 0; }
}

@keyframes blink {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
}

.empty-text {
  margin-top: 20px;
  font-size: 18px;
  color: #666;
  text-align: center;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
}

/* 响应式调整 */
@media screen and (max-width: 768px) {
  .astronaut-animation {
    height: 300px;
  }

  .astronaut {
    transform: translate(-50%, -50%) scale(0.8);
  }
}
</style>



// WEBPACK FOOTER //
// src/MultiFunctionalUploadView/MultiFunctionalUploadView.vue